import React, { useMemo, useState } from 'react';
import axios from '../config/axios';

const stepCardStyle = {
    background: 'white',
    borderRadius: '24px',
    width: 'min(720px, calc(100vw - 32px))',
    padding: '28px',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
};

export default function WebOnboardingWizard({ user, onComplete, onKidAdded }) {
    const userKey = useMemo(() => user?.user_id || user?.id || 'default', [user]);
    const [step, setStep] = useState(0);
    const [pin, setPin] = useState('');
    const [currencyName, setCurrencyName] = useState(localStorage.getItem('dadbucks_currency_name') || 'DadBucks');
    const [kidName, setKidName] = useState('');
    const [kidUsername, setKidUsername] = useState('');
    const [kidPassword, setKidPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const finish = () => {
        localStorage.setItem(`dadbucks_web_onboarding_complete_${userKey}`, 'true');
        onComplete?.();
    };

    const handleNextFromPin = () => {
        if (!/^\d{4}$/.test(pin)) {
            setError('Enter a 4-digit PIN.');
            return;
        }
        localStorage.setItem(`dadbucks_parent_pin_${userKey}`, pin);
        setError('');
        setStep(1);
    };

    const handleCurrency = () => {
        if (!currencyName.trim()) {
            setError('Enter a currency name.');
            return;
        }
        localStorage.setItem('dadbucks_currency_name', currencyName.trim());
        setError('');
        setStep(2);
    };

    const handleAddKid = async () => {
        if (!kidName.trim() || !kidUsername.trim() || !kidPassword.trim()) {
            setError('Complete all kid profile fields.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await axios.post('/api/v1/auth/register-kid', {
                name: kidName.trim(),
                username: kidUsername.trim(),
                password: kidPassword,
                household_id: user?.tenant_id,
                coppa_consent: true,
            });
            onKidAdded?.();
            finish();
        } catch (requestError) {
            console.error('Failed to create kid during onboarding:', requestError);
            setError(requestError.response?.data?.message || requestError.response?.data || 'Failed to create kid profile.');
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        {
            eyebrow: 'Step 1 of 3',
            title: 'Secure the Parent Zone',
            description: 'Create a 4-digit PIN before you approve chores or payouts.',
            body: (
                <>
                    <input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={pin}
                        onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
                        placeholder="4-digit PIN"
                        aria-label="4-digit parent PIN"
                        style={inputStyle}
                    />
                    <button style={primaryButtonStyle} onClick={handleNextFromPin}>Save PIN</button>
                </>
            ),
        },
        {
            eyebrow: 'Step 2 of 3',
            title: 'Name Your Family Currency',
            description: 'Make the reward system feel like your family’s own economy.',
            body: (
                <>
                    <input
                        type="text"
                        value={currencyName}
                        onChange={(event) => setCurrencyName(event.target.value)}
                        placeholder="DadBucks"
                        aria-label="Family currency name"
                        style={inputStyle}
                    />
                    <button style={primaryButtonStyle} onClick={handleCurrency}>Save Currency</button>
                </>
            ),
        },
        {
            eyebrow: 'Step 3 of 3',
            title: 'Add Your First Kid',
            description: 'Finish setup with one starter profile. You can add more later.',
            body: (
                <>
                    <input type="text" value={kidName} onChange={(event) => setKidName(event.target.value)} placeholder="Kid name" aria-label="Kid's name" style={inputStyle} />
                    <input type="text" value={kidUsername} onChange={(event) => setKidUsername(event.target.value)} placeholder="Username" aria-label="Kid's username" style={inputStyle} />
                    <input type="password" value={kidPassword} onChange={(event) => setKidPassword(event.target.value)} placeholder="Password" aria-label="Kid's password" style={inputStyle} />
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button style={primaryButtonStyle} onClick={handleAddKid} disabled={loading}>{loading ? 'Creating...' : 'Finish Setup'}</button>
                        <button style={secondaryButtonStyle} onClick={finish}>Skip for now</button>
                    </div>
                </>
            ),
        },
    ];

    const current = steps[step];

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }} role="dialog" aria-modal="true" aria-label={`Onboarding: ${current.title}`}>
            <div style={stepCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '24px' }}>
                    <div>
                        <div style={{ color: '#6366f1', fontWeight: 700, marginBottom: '8px' }}>{current.eyebrow}</div>
                        <h2 style={{ margin: 0, fontSize: '2rem', color: '#0f172a' }}>{current.title}</h2>
                        <p style={{ margin: '10px 0 0', color: '#475569', lineHeight: 1.6 }}>{current.description}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {steps.map((_, index) => (
                            <span key={index} style={{ width: '10px', height: '10px', borderRadius: '999px', background: index === step ? '#6366f1' : '#cbd5e1' }} />
                        ))}
                    </div>
                </div>

                <div style={{ display: 'grid', gap: '14px' }}>
                    {current.body}
                    {error && <div style={{ color: '#dc2626', fontWeight: 600 }} role="alert">{error}</div>}
                </div>
            </div>
        </div>
    );
}

const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '14px',
    border: '1px solid #cbd5e1',
    fontSize: '1rem',
    color: '#0f172a',
    background: '#f8fafc',
    boxSizing: 'border-box',
};

const primaryButtonStyle = {
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    padding: '14px 18px',
    fontWeight: 700,
    cursor: 'pointer',
};

const secondaryButtonStyle = {
    background: '#e2e8f0',
    color: '#0f172a',
    border: 'none',
    borderRadius: '14px',
    padding: '14px 18px',
    fontWeight: 700,
    cursor: 'pointer',
};
